import { useEffect, useMemo, useState } from 'react';
import { FilterCategory, FilterCategoryItem } from '../../../../core/models/filterModal';
import { LiveClass } from '../../models/LiveClassDto.model';
import { markLiveClassAsFinished } from '../../queries/LiveClasses';
import { useFetchRecordedClasses } from './useFetchRecordedClasses';

const DISCIPLINES_FILTER_CATEGORY = 'Disciplinas';
const MONTHS_FILTER_CATEGORY = 'Mês de gravação';

const DISCIPLINES_FILTER_OPTIONS: FilterCategoryItem[] = [
  {
    label: 'Biologia',
    value: 'biologia',
  },
  {
    label: 'Química',
    value: 'quimica',
  },
  {
    label: 'Física',
    value: 'fisica',
  },
  {
    label: 'Matemática',
    value: 'matematica',
  },
  {
    label: 'História',
    value: 'historia',
  },
  {
    label: 'Geografia',
    value: 'geografia',
  },
  {
    label: 'Atualidades',
    value: 'atualidades',
  },
  {
    label: 'Filosofia',
    value: 'filosofia',
  },
  {
    label: 'Sociologia',
    value: 'sociologia',
  },
  {
    label: 'Português',
    value: 'portugues',
  },
  {
    label: 'Literatura',
    value: 'literatura',
  },
  {
    label: 'Inglês',
    value: 'ingles',
  },
  {
    label: 'Espanhol',
    value: 'espanhol',
  },
  {
    label: 'Interdisciplinar',
    value: 'interdisciplinar',
  },{
    label: 'Redação',
    value: 'redacao',
  },
];

const MONTHS_FILTER_OPTIONS: FilterCategoryItem[] = [
  {
    label: 'Janeiro',
    value: 1,
  },
  {
    label: 'Fevereiro',
    value: 2,
  },
  {
    label: 'Março',
    value: 3,
  },
  {
    label: 'Abril',
    value: 4,
  },
  {
    label: 'Maio',
    value: 5,
  },
  {
    label: 'Junho',
    value: 6,
  },
  {
    label: 'Julho',
    value: 7,
  },
  {
    label: 'Agosto',
    value: 8,
  },
  {
    label: 'Setembro',
    value: 9,
  },
  {
    label: 'Outubro',
    value: 10,
  },
  {
    label: 'Novembro',
    value: 11,
  },
  {
    label: 'Dezembro',
    value: 12,
  },
];

const disciplineLabelByValue = new Map(
  DISCIPLINES_FILTER_OPTIONS.map((discipline) => [
    String(discipline.value),
    discipline.label,
  ])
);

const monthLabelByValue = new Map(
  MONTHS_FILTER_OPTIONS.map((month) => [Number(month.value), month.label])
);

function getRepeatedQueryParams(params: URLSearchParams, key: string) {
  return [...params.getAll(key), ...params.getAll(`${key}[]`)];
}

function getInitialSelectedFilters(): FilterCategory[] {
  const params = new URLSearchParams(window.location.search);
  const selectedFilters: FilterCategory[] = [];

  const disciplinas = Array.from(
    new Set(
      getRepeatedQueryParams(params, 'disciplinas').filter(
        (disciplinaId) => disciplinaId.trim() !== ''
      )
    )
  );

  if (disciplinas.length > 0) {
    selectedFilters.push({
      category_name: DISCIPLINES_FILTER_CATEGORY,
      ref: 'area',
      list: disciplinas.map((disciplinaId) => ({
        label: disciplineLabelByValue.get(disciplinaId) ?? disciplinaId,
        value: disciplinaId,
      })),
    });
  }

  const months = Array.from(
    new Set(
      getRepeatedQueryParams(params, 'months')
        .map((month) => Number(month))
        .filter((month) => Number.isInteger(month) && month >= 1 && month <= 12)
    )
  );

  if (months.length > 0) {
    selectedFilters.push({
      category_name: MONTHS_FILTER_CATEGORY,
      ref: 'startDate',
      list: months.map((month) => ({
        label: monthLabelByValue.get(month) ?? String(month),
        value: month,
      })),
    });
  }

  return selectedFilters;
}

function useRecordedClassesTab(origin?: 'challenge' | 'live') {
  const [isModalFilterOpen, setIsModalFilterOpen] = useState(false);
  const [isLoadingNextPage, setIsLoadingNextPage] = useState(false);
  const [recordedClasses, setRecordedClasses] = useState<LiveClass[]>([]);
  const [total, setTotal] = useState(0);
  const [selectedFilters, setSelectedFilters] = useState<FilterCategory[]>(
    getInitialSelectedFilters
  );

  const selectedDisciplinas = useMemo(() => {
    const disciplineFilter = selectedFilters.find(
      (filter) =>
        filter.category_name === DISCIPLINES_FILTER_CATEGORY ||
        filter.ref === 'area'
    );

    if (!disciplineFilter) {
      return [];
    }

    return Array.from(
      new Set(
        disciplineFilter.list
          .map((filterItem) => String(filterItem.value))
          .filter((filterItem) => filterItem.trim() !== '')
      )
    ).sort((disciplineA, disciplineB) =>
      disciplineA.localeCompare(disciplineB)
    );
  }, [selectedFilters]);

  const selectedMonths = useMemo(() => {
    const monthFilter = selectedFilters.find(
      (filter) =>
        filter.category_name === MONTHS_FILTER_CATEGORY ||
        filter.ref === 'startDate'
    );

    if (!monthFilter) {
      return [];
    }

    return Array.from(
      new Set(
        monthFilter.list
          .map((filterItem) => Number(filterItem.value))
          .filter((month) => Number.isInteger(month) && month >= 1 && month <= 12)
      )
    ).sort((monthA, monthB) => monthA - monthB);
  }, [selectedFilters]);

  const fetchResult = useFetchRecordedClasses(origin, {
    disciplinas: selectedDisciplinas,
    months: selectedMonths,
  });

  const filters = useMemo(() => {
    const categories: FilterCategory[] = [
      {
        category_name: DISCIPLINES_FILTER_CATEGORY,
        ref: 'area',
        list: DISCIPLINES_FILTER_OPTIONS,
      },
      {
        category_name: MONTHS_FILTER_CATEGORY,
        ref: 'startDate',
        list: MONTHS_FILTER_OPTIONS,
      },
    ];

    return categories;
  }, []);

  useEffect(() => {
    if (fetchResult.data) {
      const allClasses = fetchResult.data.pages.flatMap((page) => page.data);
      setRecordedClasses(allClasses);
      setTotal(fetchResult.data.pages[0]?.total ?? 0);
    }
  }, [fetchResult.data]);

  useEffect(() => {
    const currentUrl = new URL(window.location.href);
    const params = currentUrl.searchParams;

    params.delete('disciplinas');
    params.delete('disciplinas[]');
    params.delete('months');
    params.delete('months[]');

    selectedDisciplinas.forEach((disciplinaId) => {
      params.append('disciplinas', disciplinaId);
    });
    selectedMonths.forEach((month) => {
      params.append('months', String(month));
    });

    window.history.replaceState({}, '', currentUrl.toString());
  }, [selectedDisciplinas, selectedMonths]);

  useEffect(() => {
    const handleSetContentAsDone = (event: any) => {
      const { id } = event.detail;
      setRecordedClasses((prev) =>
        prev.map((item) => (item._id === id ? { ...item, done: true } : item))
      );
    };

    window.addEventListener('setContentAsDone', handleSetContentAsDone);

    return () => {
      window.removeEventListener('setContentAsDone', handleSetContentAsDone);
    };
  }, []);

  async function fetchNextPage() {
    setIsLoadingNextPage(true);
    await fetchResult.fetchNextPage();
    setIsLoadingNextPage(false);
  }

  const handleMarkAsDone = async (id: string, slug: string) => {
    try {
      await markLiveClassAsFinished(slug);
      window.dispatchEvent(
        new CustomEvent('setContentAsDone', {
          detail: { id },
        })
      );
    } catch (error) {
      console.error('Error marking live class as done:', error);
    }
  };

  return {
    recordedClasses,
    filteredRecordedClasses: recordedClasses,
    total,
    selectedFilters,
    setSelectedFilters,
    isModalFilterOpen,
    setIsModalFilterOpen,
    filters,
    isLoadingList: fetchResult.isLoading,
    isLoadingNextPage,
    fetchNextPage,
    handleMarkAsDone,
  };
}
export { useRecordedClassesTab };
